import { useQuery } from "@tanstack/react-query";
import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import type { UktzedEntry, UktzedNodeDetail } from "../../../../shared/api.ts";
import { formatTariffCode } from "../../../../shared/uktzed.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { dictionaryHref, nodeCode, plainTitle, uktzedQueries } from "@/lib/uktzed.ts";
import { cn } from "@/lib/utils.ts";

type AnatomyRow = { entry: UktzedEntry; from: number; to: number; key: string };

/**
 * One row per level from the chapter down to the node. Each row owns the digits its level adds to its parent:
 * the chapter `84`, the heading `82`, the subheading `10`, and so on. A level the tariff does not print as a line of
 * its own is part of the next row, so `8482 20 00 00` adds `20 00 00` to heading 8482.
 * A grouping line in the path gets a row that owns no digits.
 */
function anatomyRows(detail: UktzedNodeDetail): AnatomyRow[] {
  const entries = [...detail.path.filter((entry) => entry.level !== "section"), detail.node];
  let digits = 0;
  return entries.map((entry, index) => {
    if (entry.level === "group") return { entry, from: digits, to: digits, key: `group-${index}` };
    const row = { entry, from: digits, to: entry.id.length, key: entry.id };
    digits = entry.id.length;
    return row;
  });
}

/** Where digit number `count` (from 0) of a printed code is, or the length when it has fewer digits. */
function digitIndex(printed: string, count: number): number {
  let digit = 0;
  for (let index = 0; index < printed.length; index += 1) {
    if (!/\d/.test(printed[index] ?? "")) continue;
    if (digit === count) return index;
    digit += 1;
  }
  return printed.length;
}

/** Splits the printed code around digits `from` up to `to`, so those can be marked. An empty range marks nothing. */
function splitDigits(printed: string, from: number, to: number): [string, string, string] {
  const start = digitIndex(printed, from);
  const end = to > from ? digitIndex(printed, to - 1) + 1 : start;
  return [printed.slice(0, start), printed.slice(start, end), printed.slice(end)];
}

const ROW = "grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 rounded-lg px-2 py-1.5";

function Row({ row, code, last, clamp }: { row: AnatomyRow; code: string; last: boolean; clamp: boolean }) {
  const { t } = useI18n();
  const [before, mark, after] = splitDigits(formatTariffCode(code), row.from, row.to);
  const { entry } = row;
  const text = plainTitle(entry.title);
  const digits = (
    <span aria-hidden className="flex flex-col">
      <span className="code-mono text-[12.5px] leading-5 whitespace-pre">
        <span className="text-muted-foreground/70">{before}</span>
        {mark ? <span className="-mx-[2px] rounded-[3px] bg-primary-soft px-[2px] font-semibold text-primary-soft-foreground">{mark}</span> : null}
        <span className="text-muted-foreground/40">{after}</span>
      </span>
      <span className="text-[10px] leading-4 font-semibold tracking-[0.06em] text-muted-foreground uppercase">
        {entry.level === "group" ? t.uktzed.groupLine : t.uktzed.levelsShort[entry.level]}
      </span>
    </span>
  );
  const words = (extra?: string) => (
    <span aria-hidden className={cn("pt-px text-[12.5px] leading-[1.45] break-words", clamp && "line-clamp-2", extra)}>
      {text}
    </span>
  );
  if (entry.level === "group") {
    // A grouping line explains the rows below it but is not a node, so it does not link.
    return (
      <li className={ROW} aria-label={`${t.uktzed.groupLineHint}: ${text}`} title={text}>
        {digits}
        {words("text-muted-foreground italic")}
      </li>
    );
  }
  const name = `${t.uktzed.levels[entry.level]} ${nodeCode(entry)}`;
  return (
    <li>
      <Link
        to={dictionaryHref(entry.id)}
        aria-label={`${name}: ${text}`}
        title={text}
        className={cn(ROW, "group transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/35")}
      >
        {digits}
        {words(cn("text-foreground/85 group-hover:text-foreground", last && "font-semibold text-foreground"))}
      </Link>
    </li>
  );
}

function AnatomySkeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-2.5 py-1" aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-start gap-3">
          <Skeleton className="h-4 w-[6.25rem] shrink-0" />
          <Skeleton className="h-4 flex-1" style={{ maxWidth: `${[88, 72, 80, 64, 76][index % 5]}%` }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Explains a code level by level: each row marks the digits of its level within the full code, names the level,
 * gives its title, and opens that node in the dictionary. A grouping line in the path is a row without digits or a link.
 * `compact` shows the heading and the code itself, with a button for the levels between; the code cards of possible codes use it.
 * Titles are cut to two lines when the code's full title is behind "Full title", and in the compact form.
 * Without dictionary data (an error, or a code that is not in the tariff) it falls back to `title` as text.
 *
 * @param code - The code to explain, usually ten digits.
 * @param title - The code's tariff wording as one text, kept behind "Full title".
 * @param detail - The node, when the caller already loaded it.
 */
export function CodeAnatomy({
  code,
  title,
  detail,
  variant = "full",
  className,
}: {
  code: string;
  title?: string;
  detail?: UktzedNodeDetail;
  variant?: "full" | "compact";
  className?: string;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const query = useQuery({ ...uktzedQueries.node(code), enabled: !detail });
  const data = detail ?? query.data;
  const compact = variant === "compact";

  if (!data) {
    if (query.isPending && !detail) return <AnatomySkeleton rows={compact ? 2 : 4} />;
    return title ? <p className={cn("text-[13px] leading-relaxed text-foreground/85", compact && "text-[12.5px]", className)}>{title}</p> : null;
  }

  const rows = anatomyRows(data);
  const last = rows[rows.length - 1];
  const headline = rows.find((row) => row.entry.level === "heading") ?? rows[0];
  const shown = compact && !expanded ? rows.filter((row) => row === headline || row === last) : rows;
  const clamp = compact || (Boolean(title) && !expanded);
  const list = (
    <ol aria-label={t.uktzed.structureOf(formatTariffCode(data.node.id))} className="-mx-2 flex flex-col">
      {shown.map((row) => (
        <Row key={row.key} row={row} code={data.node.id} last={row === last} clamp={clamp} />
      ))}
    </ol>
  );

  if (compact) {
    return (
      <div className={cn("flex flex-col", className)}>
        {list}
        {shown.length < rows.length || expanded ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            className="group mt-0.5 inline-flex w-fit items-center gap-1 rounded-md text-[12px] font-semibold text-primary outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35"
          >
            {expanded ? t.uktzed.fewerLevels : t.uktzed.allLevels(rows.length)}
            <ChevronDownIcon className="size-3.5 transition-transform group-aria-expanded:rotate-180" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex min-h-6 items-center justify-between gap-2">
        <span className="text-[11px] font-semibold tracking-[0.06em] text-muted-foreground uppercase">{t.uktzed.structure}</span>
        {title ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            className="group inline-flex items-center gap-1 rounded-md px-1 text-[12px] font-semibold text-primary outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35"
          >
            {t.uktzed.fullTitle}
            <ChevronDownIcon className="size-3.5 transition-transform group-aria-expanded:rotate-180" aria-hidden />
          </button>
        ) : null}
      </div>
      {list}
      {expanded && title ? <p className="mt-1 rounded-lg bg-muted/60 px-3 py-2 text-[12.5px] leading-relaxed text-foreground/85">{title}</p> : null}
    </div>
  );
}
